import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { onLCP, onINP, onCLS } from 'web-vitals';

const IGNORED_PATHS = ['/login', '/register', '/admin', '/dashboard'];

const useAnalytics = () => {
    const location = useLocation();
    const startTimeRef = useRef(Date.now());
    const maxScrollRef = useRef(0);
    const metricsRef = useRef({});
    const hasSentPageviewRef = useRef(false);

    // 🚀 NEW: Helper function to check if the current user is an admin/author
    const checkIsInternalUser = () => {
        try {
            // Adjust this based on how your app stores the logged-in user!
            // E.g., if you store the user object in localStorage:
            const userString = localStorage.getItem('user'); 
            if (userString) {
                const user = JSON.parse(userString);
                return user.role === 'admin' || user.role === 'author';
            }
        } catch (e) {
            console.error("Error parsing user state for analytics", e);
        }
        return false;
    };

    const sendEvent = (payload) => {
        if (IGNORED_PATHS.some(path => location.pathname.startsWith(path))) return;
        
        const url = 'https://ai.dataspeckle.com/api/analytics/track';
        
        // 🚀 Fetch the flag right before sending
        const isInternal = checkIsInternalUser();

        const safePayload = {
            url_path: String(payload.url_path),
            session_id: String(payload.session_id),
            event_type: String(payload.event_type),
            screen_width: Number(payload.screen_width),
            screen_height: Number(payload.screen_height),
            referrer: payload.referrer || document.referrer || null,
            load_time_ms: payload.load_time_ms || 0,
            event_data: payload.event_data || {},
            // 🚀 NEW: Send the flag to your FastAPI backend
            is_internal: isInternal 
        };

        // 'keepalive: true' ensures this request finishes even if the user closes the tab.
        fetch(url, { 
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(safePayload),
            keepalive: true 
        }).catch(err => console.error("Tracking Error:", err));
    };

    // 1. SCROLL LISTENER
    useEffect(() => {
        if (IGNORED_PATHS.some(path => location.pathname.startsWith(path))) return;
        let throttleTimeout = null;
        
        const handleScroll = () => {
            if (throttleTimeout) return;
            throttleTimeout = setTimeout(() => {
                const scrollTop = window.scrollY;
                const docHeight = document.body.offsetHeight - window.innerHeight;
                if (docHeight > 0) {
                    const scrollPercent = Math.round((scrollTop / docHeight) * 100);
                    if (scrollPercent > maxScrollRef.current) maxScrollRef.current = scrollPercent;
                }
                throttleTimeout = null;
            }, 500); 
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [location.pathname]);

    // 2. PAGE VIEW & EXIT
    useEffect(() => {
        if (IGNORED_PATHS.some(path => location.pathname.startsWith(path))) return;

        hasSentPageviewRef.current = false;
        startTimeRef.current = Date.now();
        maxScrollRef.current = 0;
        metricsRef.current = {};

        let sessionId = localStorage.getItem('analytics_session_id');
        if (!sessionId) {
            sessionId = uuidv4();
            localStorage.setItem('analytics_session_id', sessionId);
        }

        // Send PAGEVIEW
        const timer = setTimeout(() => {
            if (hasSentPageviewRef.current) return;
            hasSentPageviewRef.current = true;
            
            const perf = window.performance?.getEntriesByType("navigation")[0];
            const loadTime = perf ? perf.domContentLoadedEventEnd : 0;

            sendEvent({
                url_path: location.pathname + location.search, 
                session_id: sessionId,
                event_type: "pageview",
                screen_width: window.innerWidth,
                screen_height: window.innerHeight,
                referrer: document.referrer || null, 
                load_time_ms: loadTime,             
                event_data: {}
            });
        }, 100);

        // Send EXIT STATS on unmount
        return () => {
            clearTimeout(timer);
            const timeSpentMs = Date.now() - startTimeRef.current;
            
            // Only send exit stats if they stayed > 1 second
            if (timeSpentMs > 1000 && hasSentPageviewRef.current) {
                sendEvent({
                    url_path: location.pathname + location.search,
                    session_id: sessionId,
                    event_type: "exit_stats",
                    screen_width: window.innerWidth,
                    screen_height: window.innerHeight,
                    event_data: {
                        time_on_page_ms: timeSpentMs,
                        scroll_depth_percent: maxScrollRef.current,
                        web_vitals: metricsRef.current 
                    }
                });
            }
        };
    }, [location.pathname]); 

    // 3. WEB VITALS
    useEffect(() => {
        const handleMetric = (metric) => {
            metricsRef.current[metric.name] = { value: metric.value, rating: metric.rating };
        };
        onLCP(handleMetric);
        onCLS(handleMetric);
        onINP(handleMetric);
    }, []); 
};

export default useAnalytics;